"use client";

import { useExperiment } from "@hopper-b2b/experiments";
import { BrandNames, Currency, FiatPrice } from "@hopper-b2b/types";
import { useUserSettingsContext } from "@hopper-b2b/user-settings";
import { getCurrencyFromStorage, isAtHotelBrand } from "@hopper-b2b/utilities";
import {
  formatCurrencyWithOptions as hopperFormatCurrency,
  formatCurrencyForShopping as hopperFormatCurrencyForShopping,
} from "@hopper-i18n/formatter";
import { useCallback, useMemo } from "react";
import { useI18nextContext } from "../I18nContext";
import { getLang } from "../utils/getLang";

const getCurrency = (defaultCurrency: Currency = Currency.USD): Currency => {
  const fromStorage = getCurrencyFromStorage();

  return fromStorage && isAtHotelBrand() ? fromStorage : defaultCurrency;
};

export function useI18nCurrency(): {
  currencyCode: string;
  currencyNameOf: (currencyCode: string) => string | undefined;
  currencySymbol: string;
  formatFiatCurrencyForShopping: (
    fiatPrice: { value: number; currencyCode: string },
    options?: Intl.NumberFormatOptions
  ) => string | undefined;
  formatFiatCurrency: (
    fiatPrice: { value: number; currencyCode: string } | undefined,
    options?: Intl.NumberFormatOptions
  ) => string | undefined;
  formatZeroAmount: (currency?: string) => string | undefined;
} {
  const ctx = useI18nextContext();
  const { preferredCurrency } = useUserSettingsContext();
  const enableCurrencySelect = useExperiment("WebHotelPreferredCurrency");

  if (ctx === undefined) throw new Error(`must be used within a I18nProvider`);
  const { language, options } = ctx;
  const { resources } = options;

  const selectedCurrency =
    (resources?.[language]?.brand as BrandNames | undefined)?.currency ??
    (resources?.["en"]?.brand as BrandNames | undefined)?.currency;

  const currencyCode = enableCurrencySelect
    ? getCurrency(preferredCurrency || selectedCurrency?.code)
    : Currency.USD;
  const currencySymbol = enableCurrencySelect
    ? selectedCurrency?.symbol || "$"
    : "$";

  const locale = getLang(selectedCurrency?.locale || "en-US", true);

  /**
   * Format a zero amount in the given currency or the user preferred currency if not provided.
   */
  const formatZeroAmount = (currency?: string) => {
    const fiat = {
      value: 0,
      currencyCode: currency ?? currencyCode,
    };
    return formatFiatCurrencyForShopping(fiat);
  };

  /**
   * Used for presenting amounts shown for advertisements. It returns amounts rounded with no decimal places
   */
  const formatFiatCurrencyForShopping = (
    fiatPrice?: Omit<FiatPrice, "currencySymbol">
  ) => {
    if (!fiatPrice) return;

    return hopperFormatCurrencyForShopping(
      fiatPrice.currencyCode,
      locale,
      fiatPrice.value
    );
  };

  function formatFiatCurrency(
    fiatPrice: Omit<FiatPrice, "currencySymbol">,
    options?: Intl.NumberFormatOptions
  ): string;
  function formatFiatCurrency(
    fiatPrice: Omit<FiatPrice, "currencySymbol"> | undefined,
    options?: Intl.NumberFormatOptions
  ): string | undefined;
  function formatFiatCurrency(
    fiatPrice: Omit<FiatPrice, "currencySymbol"> | undefined,
    options?: Intl.NumberFormatOptions
  ): string | undefined {
    if (!fiatPrice) return;
    // https://hopper-jira.atlassian.net/wiki/spaces/IEP/pages/6205308931/Tenant-specific+currency+symbols
    // `brandingSymbol` may become undefined in most cases (see the LegacySymbolOverrides table below)
    // and it is fine to pass undefined to `hopperFormatCurrency` function below.
    const brandingSymbol: string | undefined =
      selectedCurrency?.symbolOverride?.[fiatPrice.currencyCode];
    return hopperFormatCurrency({
      currencyCode: fiatPrice.currencyCode,
      locale,
      amount: fiatPrice.value,
      symbol: brandingSymbol,
      options,
    });
  }

  const currencyNames = useMemo(() => {
    return new Intl.DisplayNames([locale], { type: "currency" });
  }, [locale]);

  const currencyNameOf = useCallback(
    (currencyCode: Currency) => {
      return currencyNames.of(currencyCode);
    },
    [currencyNames]
  );

  return {
    currencyCode,
    currencyNameOf,
    currencySymbol,
    formatZeroAmount,
    formatFiatCurrencyForShopping,
    formatFiatCurrency,
  };
}
